import { useState, useEffect } from "react";
import {
  Button,
  Container,
  Stack,
  Typography,
  TextField,
  Paper,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import Page from "src/components/Page";
import FormItem from "src/components/form-item/FormItem";
import { asyncRequest } from "src/services/network";
import { alert } from "src/services/alert";

/**
 * 直推业绩奖励比例配置页面
 * 支持灵活配置每X笔循环的奖励比例
 */
export default function DirectValueRatioConfig() {
  const [config, setConfig] = useState({
    cycleLength: 3,
    ratios: [0.2, 0.3, 0.5],
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  async function fetchConfig() {
    try {
      const { data } = await asyncRequest({
        method: "GET",
        path: "/system/config/direct-value-ratio/query",
      });
      if (data) {
        setConfig({
          cycleLength: data.cycleLength ?? 3,
          ratios: data.ratios ?? [0.2, 0.3, 0.5],
        });
      }
    } catch (e) {
      console.error("获取直推业绩比例配置失败:", e);
    }
  }

  // 添加一笔比例
  const handleAddRatio = () => {
    setConfig({
      ...config,
      ratios: [...config.ratios, 0],
      cycleLength: config.cycleLength + 1,
    });
  };

  // 删除一笔比例
  const handleDeleteRatio = (index) => {
    if (config.ratios.length <= 1) {
      alert("至少需要保留一笔比例配置");
      return;
    }
    const newRatios = config.ratios.filter((_, i) => i !== index);
    setConfig({
      ...config,
      ratios: newRatios,
      cycleLength: newRatios.length,
    });
  };

  // 修改某笔比例
  const handleRatioChange = (index, value) => {
    const newRatios = [...config.ratios];
    newRatios[index] = parseFloat(value) || 0;
    setConfig({
      ...config,
      ratios: newRatios,
    });
  };

  // 校验比例之和是否为 100%
  const totalRatio = config.ratios.reduce((sum, r) => sum + Number(r), 0);
  const ratioValid = Math.abs(totalRatio - 1) < 0.0001;

  async function handleSave() {
    if (!ratioValid) {
      alert(`比例之和必须为100%，当前为${(totalRatio * 100).toFixed(2)}%`);
      return;
    }

    // 校验每个比例是否在 0-1 之间
    for (let i = 0; i < config.ratios.length; i++) {
      if (config.ratios[i] < 0 || config.ratios[i] > 1) {
        alert(`第${i + 1}笔比例必须在0%~100%之间`);
        return;
      }
    }

    setLoading(true);
    try {
      const { msg } = await asyncRequest({
        method: "POST",
        path: "/system/config/direct-value-ratio/save",
        data: {
          cycleLength: config.ratios.length,
          ratios: config.ratios,
        },
      });
      alert(msg, "success");
      fetchConfig();
    } catch (e) {
      console.error("保存直推业绩比例配置失败:", e);
    } finally {
      setLoading(false);
    }
  }

  async function handleReset() {
    setLoading(true);
    try {
      const { msg } = await asyncRequest({
        method: "POST",
        path: "/system/config/direct-value-ratio/reset",
      });
      alert(msg, "success");
      fetchConfig();
    } catch (e) {
      console.error("重置配置失败:", e);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Page title="直推业绩奖励比例配置">
      <Container maxWidth="lg">
        <Stack spacing={3} sx={{ py: 3 }}>
          <Typography variant="h4">直推业绩奖励比例配置</Typography>

          <Alert severity="info">
            <Typography variant="body2">
              配置说明：用户获得直推业绩时，按照第1笔、第2笔...的顺序循环应用比例。
              例如：配置3笔为20%、30%、50%，则第1笔奖励20%，第2笔奖励30%，第3笔奖励50%，
              第4笔重新从20%开始循环。
            </Typography>
          </Alert>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              当前配置
            </Typography>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>笔数</TableCell>
                    <TableCell>奖励比例</TableCell>
                    <TableCell align="right">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {config.ratios.map((ratio, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2">第 {index + 1} 笔</Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={ratio}
                          onChange={(e) => handleRatioChange(index, e.target.value)}
                          inputProps={{
                            step: 0.1,
                            min: 0,
                            max: 1,
                          }}
                          sx={{ width: 150 }}
                        />
                        <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                          ({(ratio * 100).toFixed(0)}%)
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteRatio(index)}
                          disabled={config.ratios.length <= 1}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddRatio}
                size="small"
              >
                添加笔数
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Alert severity={ratioValid ? "success" : "error"} sx={{ mb: 2 }}>
              当前比例之和：{(totalRatio * 100).toFixed(2)}%
              {ratioValid ? " ✓" : " ✗（必须为100%）"}
            </Alert>

            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading || !ratioValid}
              >
                {loading ? "保存中..." : "保存配置"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={loading}
              >
                重置为默认
              </Button>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              配置示例
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>示例1（默认）：</strong>3笔循环 [20%, 30%, 50%]
              </Typography>
              <Typography variant="body2" sx={{ pl: 2 }}>
                第1笔奖励20%，第2笔奖励30%，第3笔奖励50%，第4笔奖励20%...
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>示例2：</strong>5笔循环 [10%, 15%, 20%, 25%, 30%]
              </Typography>
              <Typography variant="body2" sx={{ pl: 2 }}>
                第1笔奖励10%，第2笔奖励15%，...，第5笔奖励30%，第6笔奖励10%...
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>示例3：</strong>2笔循环 [50%, 50%]
              </Typography>
              <Typography variant="body2" sx={{ pl: 2 }}>
                每笔都奖励50%
              </Typography>
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </Page>
  );
}
